/* global debounce */

if (!customElements.get('predictive-search')) {
  let shownAt = performance.now();
  let interactionAt = -Infinity;
  const rememberInteraction = (event) => {
    if (event.isTrusted) interactionAt = performance.now();
  };
  document.addEventListener('pointerdown', rememberInteraction, true);
  document.addEventListener('keydown', rememberInteraction, true);
  window.addEventListener('pageshow', () => {
    shownAt = performance.now();
    interactionAt = -Infinity;
    document.querySelectorAll('predictive-search').forEach(search => search.dismissForNavigation());
  });
  const synonyms = new Map();
  const wordPattern = /^[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*$/u;
  (theme.settings.searchSynonyms || '').split(/\r?\n/).forEach((line) => {
    const colon = line.indexOf(':');
    if (colon < 0) return;
    const key = line.slice(0, colon).trim().toLowerCase();
    const words = line.slice(colon + 1).split(',').map(word => word.trim().toLowerCase());
    if (!wordPattern.test(key) || words.some(word => !wordPattern.test(word))) return;
    const group = [...new Set([key, ...words])];
    if (group.length > 1) synonyms.set(key, group);
  });

  const expandQuery = (term) => {
    if (/[:"()]|\b(?:OR|AND|NOT)\b|(?:^|\s)-/i.test(term)) return term;
    let expanded = false;
    const words = term.trim().split(/\s+/).map((word) => {
      const group = synonyms.get(word.toLowerCase());
      if (!group) return word;
      expanded = true;
      return `(${group.join(' OR ')})`;
    });
    return expanded ? words.join(' AND ') : term;
  };

  const submitSearch = (evt, input, predictive) => {
    if (input.value.trim()) {
      try {
        const previous = JSON.parse(localStorage.getItem('terrain-search-history') || '[]');
        localStorage.setItem('terrain-search-history', JSON.stringify([input.value.trim(), ...previous.filter(q => q !== input.value.trim())].slice(0, 5)));
      } catch { /* Search remains usable when local storage is unavailable. */ }
    }
    if (
      !input.value.trim().length
      || predictive?.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
    ) {
      evt.preventDefault();
      return;
    }
    if (predictive) predictive.dismissForNavigation();
    else input.blur();
    const original = input.value.trim();
    const expanded = expandQuery(original);
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview && !evt.target.querySelector('[name="preview_theme_id"]')) {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = 'preview_theme_id'; input.value = preview;
      evt.target.append(input);
    }
    if (expanded !== original) {
      const fields = { q: expanded, st: original };
      input.removeAttribute('name');
      const hiddenInputs = Object.entries(fields).map(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = value;
        evt.target.append(input);
        return input;
      });
      // Native GET submission constructs its entry list before this task runs.
      setTimeout(() => {
        hiddenInputs.forEach(input => input.remove());
        input.name = 'q';
      }, 0);
    }
  };

  // R35c: plain result-page forms share R33 submission only.
  document.addEventListener('submit', (event) => {
    if (event.target.matches('form[data-terrain-search-submit]')) {
      submitSearch(event, event.target.querySelector('.js-search-input'));
    }
  });

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.requestVersion = 0;
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
      document.removeEventListener('pointerdown', this.outsideHandler);
    }

    addListeners() {
      this.outsideHandler = (event) => {
        if (this.interacting && !this.contains(event.target)) this.close();
      };
      document.addEventListener('pointerdown', this.outsideHandler);
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('click', this.handleFocus.bind(this));
      const onInput = debounce((event, version) => {
        if (version === this.requestVersion && this.interacting) this.handleInput(event);
      });
      this.input.addEventListener('input', (event) => {
        if (this.allowInteraction(event)) onInput(event, this.requestVersion);
      });
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    expandQuery(term) {
      return expandQuery(term);
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      submitSearch(evt, this.input, this);
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput(event) {
      if (!this.allowInteraction(event)) return;
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    allowInteraction(event) {
      if (!event?.isTrusted || performance.now() - shownAt < 1000) return false;
      if (event.type === 'focus' && performance.now() - interactionAt > 800) return false;
      this.interacting = true;
      return true;
    }

    dismissForNavigation() {
      this.close();
      this.input.blur();
      const header = this.closest('store-header');
      if (header?.classList.contains('search-is-visible')) {
        header.classList.remove('search-is-visible');
        header.classList.add('search-is-collapsed');
        header.querySelectorAll('.js-show-search,.js-show-search-left').forEach(button => button.setAttribute('aria-expanded', 'false'));
      }
      document.documentElement.classList.remove('terrain-search-open');
    }

    showRecentSearches() {
      if (!this.interacting) return;
      let recent;
      try { recent = JSON.parse(localStorage.getItem('terrain-search-history') || '[]'); } catch { return; }
      if (!Array.isArray(recent) || !recent.length) return;
      const box = document.createElement('div');
      box.className = 'ps predictive-search absolute bg-theme-bg text-theme-text text-start terrain-recent';
      const title = document.createElement('h3');
      title.className = 'terrain-recent-title'; title.textContent = 'Letzte Suchen'; box.append(title);
      const chips = document.createElement('div');
      chips.className = 'terrain-recent-chips'; box.append(chips);
      recent.filter(q => typeof q === 'string').slice(0, 5).forEach(query => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'terrain-recent-chip'; button.textContent = query;
        button.addEventListener('click', () => { this.input.value = query; this.input.focus(); this.getResults(query); });
        chips.append(button);
      });
      const clear = document.createElement('button');
      clear.type = 'button'; clear.className = 'terrain-recent-clear'; clear.textContent = 'Verlauf löschen';
      clear.addEventListener('click', () => {
        try { localStorage.removeItem('terrain-search-history'); } catch { /* Storage may be disabled. */ }
        this.close();
      });
      chips.append(clear);
      this.removeAttribute('results');
      this.results.replaceChildren(box); this.open(); this.resetBtn.hidden = false;
    }

    handleFocus(event) {
      if (!this.allowInteraction(event)) return;
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        this.showRecentSearches();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      if (!this.interacting) return;
      const requestVersion = this.requestVersion || 0;
      this.setLiveRegionLoadingState();
      const expandedTerm = this.expandQuery(searchTerm);

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(expandedTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(expandedTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams;
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const noResults = resultsEl.querySelector('.predictive-search__no-results');
        if (noResults && expandedTerm !== searchTerm) {
          noResults.dataset.expandedTerm = expandedTerm;
        }
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        if (this.getQuery() !== searchTerm || requestVersion !== this.requestVersion || !this.interacting) return;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      const noResults = this.results.querySelector('[data-expanded-term]');
      if (noResults) {
        noResults.textContent = noResults.textContent.replace(noResults.dataset.expandedTerm, this.getQuery());
      }
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.style.setProperty('--terrain-search-bottom', `${this.input.getBoundingClientRect().bottom}px`);
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      const panel = this.querySelector('.ps');
      if (panel) this.style.setProperty('--terrain-panel-top', `${Math.max(0, panel.getBoundingClientRect().top)}px`);
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);

    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      this.interacting = false;
      this.requestVersion = (this.requestVersion || 0) + 1;
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);

    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
